import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { Pressable, View } from 'react-native';
import {
  NestableDraggableFlatList,
  NestableScrollContainer,
  ScaleDecorator,
  type RenderItemParams,
} from 'react-native-draggable-flatlist';
import { Paragraph, XStack, YStack } from 'tamagui';
// Lucide replaces the hand-rolled CameraIcon/XIcon components and the
// inline ChevronLeftIcon/ChevronRightIcon copies (identical lucide
// marks). The inline copies kept their 2.5 stroke, camera its 1.75,
// via strokeWidth at the call sites.
import {
  Camera as CameraIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  X as XIcon,
} from 'lucide-react-native';

import { Text } from '@/components/text';
import { AuthenticatedImage } from '@/components/authenticated-image';
import { SolidSurface, useGlassAccentColor } from '@/components/glass-surface';
import { LoadingIndicator } from '@/components/loading-indicator';
import { ScreenContainer } from '@/components/screen-container';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { ScreenHeader } from '@/components/screen-header';
import { useMyProfile } from '@/hooks/use-my-profile';
import { api, type OwnProfile } from '@/lib/api';
import { buildProfilePayload } from '@/lib/profile-draft';
import { MAX_PHOTOS } from '@/lib/content-limits';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '@/lib/photo-errors';
import { photoIdFromPath, photoPath } from '@/lib/photo-path';
import { pickShrunkPhoto } from '@/lib/pick-photo';
import { borderWidth, radius, space } from '@/theme/tokens.generated';
import { hit, slopFor } from '@/lib/hit-slop';

// Issue #80's profile-photos split -- this section was previously
// inline in profile/index.tsx (that screen's own "several smaller
// sections" doc comment tells the fuller story of why). Photos already
// saved independently of the rest of that form -- every add/remove/
// reorder here was already its own immediate mutation, never gated
// behind the main "Save profile" button -- so extracting it is a real,
// self-contained split: no cross-screen state to lift, no shared
// unsaved-edit risk. Reached from Profile via a "Manage photos" link,
// not the drawer (it's a Profile sub-section, not a top-level app
// destination).
const GRID_COLUMNS = 3;
const GRID_GAP = 8;

export function ProfilePhotos() {
  const router = useRouter();
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();
  const queryClient = useQueryClient();

  const ownProfile = useMyProfile();

  const [photoIds, setPhotoIds] = useState<string[]>([]);
  const hasPrefilled = useRef(false);

  useEffect(() => {
    if (hasPrefilled.current || !ownProfile.isSuccess) return;

    hasPrefilled.current = true;
    setPhotoIds(ownProfile.data.photos.map(photoIdFromPath));
  }, [ownProfile.isSuccess, ownProfile.data]);

  function applyProfileResponse(profile: OwnProfile) {
    queryClient.setQueryData(queryKeys.meProfile(), profile);
  }

  // Every field here except `photos` comes straight from the
  // last-saved profile (ownProfile.data), never a live in-progress
  // edit -- this screen has no handle/radius/prompts form state of its
  // own to send, and PATCH /me/profile has no partial-merge semantics
  // (every call must send the full desired state, see photo-path.ts's
  // own doc comment). This is exactly what profile/index.tsx's own
  // buildProfilePatch sends too, for every field this screen doesn't
  // touch: those local fields only ever start as a copy of
  // ownProfile.data in the first place.
  function buildPhotosPatch(nextPhotoIds: string[]) {
    if (!ownProfile.isSuccess) {
      throw new Error('Profile must exist before photos can be edited.');
    }

    // The module, not a hand-rolled passthrough: the previous version
    // copied every field EXCEPT gender/ageRange/hideDemographics, whose
    // api.updateProfile defaults then wiped the account's demographics
    // on every photo save (live bug found while extracting this module).
    // buildProfilePayload rides them through from current.
    return buildProfilePayload(ownProfile.data, { photos: nextPhotoIds });
  }

  const addPhoto = useMutation({
    mutationFn: async () => {
      const photo = await pickShrunkPhoto();
      const uploaded = await api.uploadPhoto(photo.base64, photo.mimeType);
      const nextPhotoIds = [...photoIds, uploaded.id];

      let profile;
      try {
        profile = await api.updateProfile(buildPhotosPatch(nextPhotoIds));
      } catch (patchError) {
        // The blob is already stored and counts against the 6-photo cap
        // whether or not it's attached to the profile -- without this
        // rollback, a dropped connection here orphans a blob the user
        // can never see or remove (same reasoning as this mutation's
        // prior home in profile/index.tsx, PR #185's own review).
        await api.deletePhoto(uploaded.id).catch(() => {});
        throw patchError;
      }

      return { profile, nextPhotoIds };
    },
    onSuccess: ({ profile, nextPhotoIds }) => {
      applyProfileResponse(profile);
      setPhotoIds(nextPhotoIds);
    },
  });

  const removePhoto = useMutation({
    mutationFn: async (id: string) => {
      await api.deletePhoto(id);
      const nextPhotoIds = photoIds.filter((existing) => existing !== id);
      const profile = await api.updateProfile(buildPhotosPatch(nextPhotoIds));

      return { profile, nextPhotoIds };
    },
    onSuccess: ({ profile, nextPhotoIds }) => {
      applyProfileResponse(profile);
      setPhotoIds(nextPhotoIds);
    },
  });

  const reorderPhoto = useMutation({
    mutationFn: async (nextPhotoIds: string[]) => {
      const profile = await api.updateProfile(buildPhotosPatch(nextPhotoIds));
      return { profile, nextPhotoIds };
    },
    onSuccess: ({ profile, nextPhotoIds }) => {
      applyProfileResponse(profile);
      setPhotoIds(nextPhotoIds);
    },
  });

  // One flag gates every photo action, not just each mutation's own
  // isPending -- add/remove/reorder all read-then-write the same
  // photoIds array and PATCH the same resource, so two in flight at
  // once race on which one's stale `photoIds` closure wins the final
  // write (same reasoning as this logic's prior home in
  // profile/index.tsx, PR #185's own review).
  const anyPhotoMutationPending = addPhoto.isPending || removePhoto.isPending || reorderPhoto.isPending;

  function movePhoto(index: number, direction: -1 | 1) {
    if (anyPhotoMutationPending) return;

    const target = index + direction;
    if (target < 0 || target >= photoIds.length) return;

    const next = [...photoIds];
    [next[index], next[target]] = [next[target], next[index]];
    reorderPhoto.mutate(next);
  }

  const atCap = photoIds.length >= MAX_PHOTOS;
  const [gridWidth, setGridWidth] = useState(0);
  const tileSize = gridWidth > 0 ? (gridWidth - GRID_GAP * (GRID_COLUMNS - 1)) / GRID_COLUMNS : 0;
  const removingId = removePhoto.isPending ? removePhoto.variables : undefined;

  return (
    <ScreenContainer scrollUnderDock>
      <NestableScrollContainer
        style={{ flex: 1 }}
        {...dockScroll}
        contentContainerStyle={{ paddingTop: space[6], paddingBottom: space[6] + dockClearance, gap: space[5] }}
      >
        <ScreenHeader
          title="Photos"
          subtitle="Only visible to people you've mutually connected with — this is the reveal payoff."
          onBack={() => router.back()}
        />

        <YStack gap="$2">
          {/* No section label here -- issue #298: this screen's own H1
              ("Photos") sits one gap above, with nothing else between
              them. A second "Photos" label added no information a
              reader didn't already have. */}

          {/* Always measures gridWidth (not gated on photoIds.length),
              so the empty-state tile below sizes itself off the SAME
              math the real grid uses -- one continuous visual scale
              whether zero or six photos exist, not a jarring size jump
              the moment the first photo lands. */}
          <View onLayout={(e) => setGridWidth(e.nativeEvent.layout.width)}>
            {photoIds.length === 0
              ? tileSize > 0 && (
                  <EmptyPhotosTile
                    size={tileSize * GRID_COLUMNS + GRID_GAP * (GRID_COLUMNS - 1)}
                    pending={addPhoto.isPending}
                    onPress={() => addPhoto.mutate()}
                  />
                )
              : tileSize > 0 && (
                  <NestableDraggableFlatList
                    data={photoIds}
                    keyExtractor={(id) => id}
                    numColumns={GRID_COLUMNS}
                    columnWrapperStyle={{ gap: GRID_GAP, marginBottom: GRID_GAP }}
                    scrollEnabled={false}
                    onDragEnd={({ data }) => {
                      if (!anyPhotoMutationPending) reorderPhoto.mutate(data);
                    }}
                    renderItem={({ item: id, getIndex, drag, isActive }: RenderItemParams<string>) => {
                      const index = getIndex() ?? 0;

                      return (
                        <ScaleDecorator>
                          <Pressable
                            accessibilityLabel={`Photo ${index + 1} of ${photoIds.length}. Press and hold to drag and reorder.`}
                            onLongPress={anyPhotoMutationPending ? undefined : drag}
                            disabled={anyPhotoMutationPending}
                            style={{ width: tileSize, height: tileSize, opacity: isActive ? 0.85 : 1 }}
                          >
                            <AuthenticatedImage
                              path={photoPath(id)}
                              accessibilityLabel={`Photo ${index + 1} of ${photoIds.length}`}
                              width={tileSize}
                              aspectRatio={1}
                              objectFit="cover"
                              borderRadius="$sm"
                            />
                            <Pressable
                              accessibilityRole="button"
                              accessibilityLabel="Remove photo"
                              {...hit(8)}
                              disabled={anyPhotoMutationPending}
                              onPress={() => removePhoto.mutate(id)}
                              style={{
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                width: 44,
                                height: 44,
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: anyPhotoMutationPending && removingId !== id ? 0.5 : 1,
                              }}
                            >
                              <View
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: radius.md,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(13, 11, 8, 0.55)',
                                }}
                              >
                                {removingId === id ? (
                                  <LoadingIndicator label="Removing photo" size="small" color="white" />
                                ) : (
                                  <XIcon size={16} strokeWidth={1.75} color="white" />
                                )}
                              </View>
                            </Pressable>
                            <XStack position="absolute" bottom={4} left={4} right={4} justifyContent="space-between">
                              <Pressable
                                accessibilityRole="button"
                                accessibilityLabel="Move photo earlier"
                                {...hit(slopFor(24))}
                                disabled={index === 0 || anyPhotoMutationPending}
                                onPress={() => movePhoto(index, -1)}
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: radius.md,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(13, 11, 8, 0.55)',
                                  opacity: index === 0 || anyPhotoMutationPending ? 0.4 : 1,
                                }}
                              >
                                <ChevronLeftIcon size={16} strokeWidth={1.75} color="white" />
                              </Pressable>
                              <Pressable
                                accessibilityRole="button"
                                accessibilityLabel="Move photo later"
                                {...hit(slopFor(24))}
                                disabled={index === photoIds.length - 1 || anyPhotoMutationPending}
                                onPress={() => movePhoto(index, 1)}
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: radius.md,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(13, 11, 8, 0.55)',
                                  opacity: index === photoIds.length - 1 || anyPhotoMutationPending ? 0.4 : 1,
                                }}
                              >
                                <ChevronRightIcon size={16} strokeWidth={1.75} color="white" />
                              </Pressable>
                            </XStack>
                          </Pressable>
                        </ScaleDecorator>
                      );
                    }}
                  />
                )}
          </View>

          {removePhoto.isError && (
            <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta">
              {errorMessage(removePhoto.error)}
            </Paragraph>
          )}

          {reorderPhoto.isError && (
            <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta">
              {errorMessage(reorderPhoto.error)}
            </Paragraph>
          )}

          {/* photoIds.length > 0 -- with zero photos, EmptyPhotosTile
              above already IS the add affordance (a large, real tap
              target, not this small text link); showing both would be
              a redundant second "Add photo" action stacked on top of
              the first. */}
          {photoIds.length > 0 && (
            <SolidSurface style={{ padding: space[4], alignItems: 'center' }}>
              {atCap ? (
                <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
                  Six photos is the limit.
                </Text>
              ) : (
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{ busy: addPhoto.isPending }}
                  aria-busy={addPhoto.isPending}
                  {...hit({ vertical: slopFor(32) })}
                  disabled={anyPhotoMutationPending}
                  onPress={() => addPhoto.mutate()}
                  style={{ paddingVertical: space[1] }}
                >
                  {addPhoto.isPending ? (
                    <LoadingIndicator />
                  ) : (
                    <Text color={anyPhotoMutationPending ? '$colorTertiary' : '$colorLink'}>Add photo</Text>
                  )}
                </Pressable>
              )}
            </SolidSurface>
          )}

          {addPhoto.isError && (
            <XStack
              backgroundColor="$feedbackErrorBg"
              borderColor="$feedbackErrorBorder"
              borderWidth={borderWidth.hairline}
              borderRadius="$sm"
              px="$3"
              py="$2"
            >
              <Text color="$feedbackErrorText" fontSize="$meta" lineHeight="$meta">
                {errorMessage(addPhoto.error)}
              </Text>
            </XStack>
          )}
        </YStack>
      </NestableScrollContainer>
    </ScreenContainer>
  );
}

// Founder, 2026-09-19: "I don't like dead space... make it feel slick
// and modern" -- with zero photos, this screen used to render a tiny
// centered "Add photo" text link inside a SolidSurface pill, then
// nothing else for the rest of the viewport (this app's own composition
// rule: "an empty state owns the viewport it is in... a small centred
// block... reads as a page that failed to load"). This tile is sized to
// exactly match the real 3-column grid's own full width (see its call
// site's `size` prop -- tileSize * GRID_COLUMNS + the inter-column
// gaps), so the transition from "no photos" to "first photo added"
// reads as one continuous surface growing into a grid, not a small
// pill getting replaced by an unrelated-looking layout.
function EmptyPhotosTile({ size, pending, onPress }: { size: number; pending: boolean; onPress: () => void }) {
  const accentColor = useGlassAccentColor();

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Add your first photo"
      accessibilityState={{ busy: pending }}
      aria-busy={pending}
      disabled={pending}
      onPress={onPress}
    >
      <SolidSurface
        borderRadius="$md"
        style={{
          width: size,
          // A real photo tile is a square (numColumns=3 grid cells);
          // this empty tile keeps that same aspect ratio at full grid
          // width so it reads as "the grid, before it has content" --
          // not an arbitrary banner shape.
          height: size,
          alignItems: 'center',
          justifyContent: 'center',
          gap: space[3],
        }}
      >
        {pending ? (
          <LoadingIndicator />
        ) : (
          <>
            {/* alignSelf="center" is NOT redundant with the SolidSurface
                style prop's own alignItems:'center' above -- confirmed
                via real DOM measurement (getBoundingClientRect), not
                assumed: SolidSurface wraps its children in its own
                internal `<View position="relative">` (glass-surface.tsx),
                and a plain RN View defaults to `alignItems: 'stretch'`
                regardless of what the OUTER SolidSurface YStack's own
                alignItems is set to -- that only positions SolidSurface's
                own direct child within ITS parent, not this tile's
                actual icon/text children within THAT inner wrapper. A
                fixed-size child (this 40x40 SVG) can't stretch, so under
                the inherited 'stretch' default it fell back to the
                cross-axis START (flush against the tile's left edge,
                measured at x=25 in a 342px-wide tile) instead of center
                -- YStack below doesn't have this problem only because it
                stretches to 100% width by default (no explicit width of
                its own) and THEN centers its own text children within
                that full-width box via its own alignItems="center". */}
            <View style={{ alignSelf: 'center' }}>
              <CameraIcon size={24} strokeWidth={1.75} color={accentColor} />
            </View>
            <YStack alignItems="center" gap="$1">
              <Text color="$color" fontWeight="$semibold">
                Add your first photo
              </Text>
              <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta" textAlign="center">
                Only visible once you&apos;ve mutually connected with someone.
              </Text>
            </YStack>
          </>
        )}
      </SolidSurface>
    </Pressable>
  );
}
